import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import * as Dialog from "@radix-ui/react-dialog"
import { StyledDropdownMenuTrigger } from "components/elements"
import { useMutation } from "@blitzjs/rpc"
import { useAuth } from "app/auth/hooks/useAuth"
import Link from "next/link"
import { ManageAccountModal } from "app/auth/components/ManageAccountModal"
import {
  STATIC_ASSETS_CLOUDFRONT_URL,
  TES_NATIONAL_EXPLORER_DATA_GLOSSARY_LINK,
  TES_NATIONAL_EXPLORER_FAQ_LINK,
  TES_NATIONAL_EXPLORER_DATA_DOWNLOAD_LINK,
  TES_NATIONAL_EXPLORER_DEMO_LINK,
  TES_NATIONAL_EXPLORER_STARTER_GUIDE_LINK,
  TES_NATIONAL_EXPLORER_RESOURCES_TAB_LINK,
  TES_NATIONAL_EXPLORER_METHODOLOGY_LINK,
  TES_NATIONAL_EXPLORER_HOME_LINK,
  TES_NATIONAL_EXPLORER_CONTACT_LINK,
  TES_NATIONAL_EXPLORER_SPANISH_HOME_LINK,
} from "app/constants"
import { useContext, useEffect, useState } from "react"
import { useSetAtom } from "jotai"
import { reportDropdownOpen } from "app/state"
import { CityContext } from "app/features/regional-map/regional-map.state"
import logout from "app/auth/mutations/logout"
import { MapMenuSwitchButton } from "app/features/i18n/components/map-menu-switch-button"
import { ChevronRightIcon } from "@radix-ui/react-icons"
import { RouteUrlObject } from "blitz"
import { useCurrentLanguage } from "app/features/i18n/i18n.hooks"
import { useTranslation } from "react-i18next"
import { DEFAULT_LANGUAGE } from "app/features/i18n/i18n.constants"

export type MenuItem = {
  name?: string
  link: RouteUrlObject | string
  image?: string
  component?: React.FC<{ link: string }>
  type: "item"
}

export type NestedMenuItem = {
  name: string
  items: MenuItem[]
  type: "nested"
}

const ENGLISH_HOME_ITEM: MenuItem[] = [
  {
    name: "common:home",
    link: TES_NATIONAL_EXPLORER_HOME_LINK,
    type: "item",
  },
]

const SPANISH_HOME_ITEM: MenuItem[] = [
  {
    name: "common:home",
    link: TES_NATIONAL_EXPLORER_SPANISH_HOME_LINK,
    type: "item",
  },
]

const CONTACT_ITEM: MenuItem[] = [
  {
    name: "common:contact_us",
    link: TES_NATIONAL_EXPLORER_CONTACT_LINK,
    type: "item",
  },
]

const METHODS_AND_DATA_ITEMS: NestedMenuItem[] = [
  {
    name: "common:methods_and_data",
    items: [
      {
        name: "common:methods",
        link: TES_NATIONAL_EXPLORER_METHODOLOGY_LINK,
        type: "item",
      },
      {
        name: "common:data_glossary",
        link: TES_NATIONAL_EXPLORER_DATA_GLOSSARY_LINK,
        type: "item",
      },
      {
        name: "common:faqs",
        link: TES_NATIONAL_EXPLORER_FAQ_LINK,
        type: "item",
      },
      {
        name: "common:data_download",
        link: TES_NATIONAL_EXPLORER_DATA_DOWNLOAD_LINK,
        type: "item",
      },
    ],
    type: "nested",
  },
]

const RESOURCES_ITEMS: NestedMenuItem[] = [
  {
    name: "common:resources",
    items: [
      {
        name: "common:watch_demo",
        link: TES_NATIONAL_EXPLORER_DEMO_LINK,
        type: "item",
      },
      {
        name: "common:starter_guide",
        link: TES_NATIONAL_EXPLORER_STARTER_GUIDE_LINK,
        type: "item",
      },
      {
        name: "common:more_resources",
        link: TES_NATIONAL_EXPLORER_RESOURCES_TAB_LINK,
        type: "item",
      },
    ],
    type: "nested",
  },
]

const LANGUAGE_ITEMS: NestedMenuItem[] = [
  {
    name: "common:change_language",
    items: [
      {
        component: MapMenuSwitchButton,
        link: "en",
        type: "item",
      },
      {
        component: MapMenuSwitchButton,
        link: "es",
        type: "item",
      },
    ],
    type: "nested",
  },
]

const DropdownMenuItem = (item: MenuItem) => {
  const { t } = useTranslation(["common"])
  return item?.component ? (
    <DropdownMenu.Item>{item.component({ link: item.link as string })}</DropdownMenu.Item>
  ) : (
    <DropdownMenu.Item>
      <Link href={item.link}>
        <p className="py-2 pl-4 pr-6 font-medium text-brand-green-dark decoration-[#f9c793] decoration-4 underline-offset-8 hover:underline">
          {item.image && (
            <img
              className="-mt-1 mr-3 inline w-5"
              src={`${STATIC_ASSETS_CLOUDFRONT_URL}/${item.image}`}
            />
          )}
          {item?.name && t(item.name)}
        </p>
      </Link>
    </DropdownMenu.Item>
  )
}

export default function NavMenu({ displayText }: { displayText?: string }) {
  const [open, setOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const setReportDropdownOpen = useSetAtom(reportDropdownOpen)
  const city = useContext(CityContext)!
  const isNational = !city?.id
  const [logoutMutation] = useMutation(logout)
  const { isAuthenticated } = useAuth()
  const [lang] = useCurrentLanguage()
  const isEnglishMap = lang === DEFAULT_LANGUAGE
  const { t } = useTranslation(["common"])
  const NATIONAL_MAP_MENU_ITEMS: (MenuItem | NestedMenuItem)[] = [
    ...(isEnglishMap
      ? [...ENGLISH_HOME_ITEM, ...METHODS_AND_DATA_ITEMS, ...RESOURCES_ITEMS, ...CONTACT_ITEM]
      : [...SPANISH_HOME_ITEM]),
    ...(isNational ? LANGUAGE_ITEMS : []),
  ]

  useEffect(() => {
    if (open) setReportDropdownOpen(false)
  }, [open])

  return (
    <Dialog.Root open={deleteOpen} onOpenChange={setDeleteOpen}>
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <StyledDropdownMenuTrigger aria-label="Open navigation menu">
          {displayText}
        </StyledDropdownMenuTrigger>
        <DropdownMenu.Content className="z-10 pr-4 text-body" align="start">
          <div className="mt-4 rounded bg-white py-2 text-left shadow-lg">
            {NATIONAL_MAP_MENU_ITEMS.map((item) =>
              item.type === "nested" ? (
                <DropdownMenu.Sub key={item.name}>
                  <DropdownMenu.SubTrigger className="cursor-default py-1.5 pl-4 pr-16 font-medium text-brand-green-dark decoration-[#f9c793] decoration-4 underline-offset-8 hover:underline">
                    {t(item.name)} <ChevronRightIcon className="-mt-1 inline" />
                  </DropdownMenu.SubTrigger>
                  <DropdownMenu.SubContent>
                    <div className="bg-white pt-1.5 text-left shadow">
                      {item.items.map((subItem, index) => (
                        <div key={index} className="pb-1.5">
                          <DropdownMenuItem {...subItem} />
                        </div>
                      ))}
                    </div>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Sub>
              ) : (
                <DropdownMenuItem key={item.name} {...item} />
              )
            )}
            {city && city.id !== "national" && isAuthenticated ? (
              <>
                <DropdownMenu.Item
                  className="cursor-pointer py-2 pl-4 pr-16 text-base font-medium text-brand-green-dark decoration-[#f9c793] decoration-4 underline-offset-8 hover:underline"
                  onSelect={() => setDeleteOpen(true)}
                >
                  Manage account
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="cursor-pointer py-2 pl-4 pr-16 text-base font-medium text-brand-green-dark decoration-[#f9c793] decoration-4 underline-offset-8 hover:underline"
                  onSelect={() => {
                    logoutMutation()
                  }}
                >
                  {t("common:logout")}
                </DropdownMenu.Item>
              </>
            ) : null}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {deleteOpen && <ManageAccountModal close={() => setDeleteOpen(false)} />}
    </Dialog.Root>
  )
}
